import type { PowerMeter } from '@/features/controller/configuration/models/appConfigSchema'
import type {
  ExternalModbusClientReference,
  LegacyModbusMeter,
  LegacyModbusTcpConnection,
  NamedModbusConnection,
  NamedModbusSerialClient,
  NamedModbusTcpClient,
  RaspiPropertiesConfig,
  TillquistConfig,
} from '@/features/controller/configuration/models/raspiProperties/types'
import type { ControllerDescription } from '@/features/controller/types'

export enum DeviceNodeType {
  Tillquist,
  /**
   * A power meter declared in `app.json`'s `powerMeters`. A Schneider or Algodue model is on the basic
   * devices port; these are auto-detected by default and only declared when the defaults need
   * overriding. A Generic one only adds settings to a meter configured elsewhere.
   */
  AppConfigPowerMeter,
  LegacyModbus,
  /**
   * A device attached to the same named connection through a configuration flex-portal does not
   * model (`controller.metering.*`, `controller.steering.*`, `controller.nwBattery`). Read-only: it
   * answers what else talks to this endpoint.
   */
  ExternalModbusDevice,
}

export interface ConfigTreeNode {
  id: string
  label: string
  errors?: string[]
}

export interface ControllerNode extends ConfigTreeNode {
  controllerDescription: ControllerDescription
}

interface DeviceNodeBase extends ConfigTreeNode {
  connectionId: string
  model: string
  type: DeviceNodeType
}

export interface TillquistPowerMeterNode extends DeviceNodeBase {
  type: DeviceNodeType.Tillquist
  config: TillquistConfig
}

export interface AppConfigPowerMeterNode extends DeviceNodeBase {
  type: DeviceNodeType.AppConfigPowerMeter
  config: PowerMeter
  /**
   * Due to controller implementation, Generic power meter can represent additional Tillquist settings (MA & values).
   * If Tillquist is in use and this is a "Generic" power meter, then this represents those Tillquist settings.
   */
  isTillquistSettingsNode?: boolean
}

export interface LegacyModbusPowerMeterNode extends DeviceNodeBase {
  type: DeviceNodeType.LegacyModbus
  config: LegacyModbusMeter
}

export interface ExternalModbusDeviceNode extends DeviceNodeBase {
  type: DeviceNodeType.ExternalModbusDevice
  config: ExternalModbusClientReference
}

/**
 * A device on a connection. Mostly power meters, but a named Modbus connection can also carry
 * metering channels, steering and an NW battery, which are shown read-only.
 *
 * Discriminated by `type`, so a row's own kind says which `config` it carries.
 */
export type DeviceNode =
  | TillquistPowerMeterNode
  | AppConfigPowerMeterNode
  | LegacyModbusPowerMeterNode
  | ExternalModbusDeviceNode

export enum DeviceConnectionNodeType {
  /** `portName`: the auto-detected basic devices (Schneider and Algodue meters, KMtronic relays) */
  BasicSerial,
  /** `serialPort.*`: the advanced devices (Tillquist, serial Modbus meters) */
  LegacySerial,
  LegacyModbusTcp,
  NamedModbusTcp,
  NamedModbusSerial,
}

interface DeviceConnectionNodeBase extends ConfigTreeNode {
  type: DeviceConnectionNodeType
}

export type SerialAndModbusConfig = Pick<RaspiPropertiesConfig, 'legacySerialPortConfig' | 'legacyModbusSerialConfig'>

export interface BasicSerialConnectionNode extends DeviceConnectionNodeBase {
  type: DeviceConnectionNodeType.BasicSerial
  config: Pick<RaspiPropertiesConfig, 'basicDevicesSerialPort'>
}

export interface LegacySerialConnectionNode extends DeviceConnectionNodeBase {
  type: DeviceConnectionNodeType.LegacySerial
  config: SerialAndModbusConfig
}

export interface LegacyModbusTcpConnectionNode extends DeviceConnectionNodeBase {
  type: DeviceConnectionNodeType.LegacyModbusTcp
  config: LegacyModbusTcpConnection
}

export interface NamedModbusTcpConnectionNode extends DeviceConnectionNodeBase {
  type: DeviceConnectionNodeType.NamedModbusTcp
  config: NamedModbusConnection & { namedClient: NamedModbusTcpClient }
}

export interface NamedModbusSerialConnectionNode extends DeviceConnectionNodeBase {
  type: DeviceConnectionNodeType.NamedModbusSerial
  config: NamedModbusConnection & { namedClient: NamedModbusSerialClient }
}

export type AnyNamedModbusConnectionNode = NamedModbusTcpConnectionNode | NamedModbusSerialConnectionNode

/** A connection the devices hang off, discriminated by `type` the way the device rows are. */
export type DeviceConnectionNode =
  | BasicSerialConnectionNode
  | LegacySerialConnectionNode
  | LegacyModbusTcpConnectionNode
  | AnyNamedModbusConnectionNode
